import { useContext, useMemo } from "react";
import { CircularProgress, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ReportCard from "components/ReportCard";
import ReportDowntimeBarChart from "components/ReportDowntimeBarChart";
import { DisplayInOptionsEnum } from "constants/constants";
import { CorporateDashboardContext } from "contexts/CorporateDashboardContext/CorporateDashboardContext";
import { MillsEntity } from "entities/Mills/MillsEntity";
import { IMillReportCardProps } from "pages/Corporate/Locations/MillDashboards/MillDashboardInterfaces";
import { handleNameTranslation } from "utilities/localesUtilities";
import { useTranslation } from "react-i18next";
import { mapCorporateFiltersToMillFilters } from "../../../utilities/mapCorporateFiltersToMillFilters";
import { useStyles } from "./FunctionalLocationsPopover.styles";

const FunctionalLocationsPopover = ({ countryCode }: IMillReportCardProps) => {
    const classes = useStyles();
    const { i18n } = useTranslation();

    const {
        filters,
        selectedFailureCode,
        selectedFailureType,
        selectedMachineCenterPopoverData,
        handleSetSelectedMachineCenterPopoverData,
        totalDowntimeSeconds,
    } = useContext(CorporateDashboardContext);

    const millFilters = useMemo(() => mapCorporateFiltersToMillFilters(filters), [filters]);
    const displayIn = filters.displayIn;

    const { downtimeSummaryPerFunctionalLocation, loading } =
        MillsEntity.GetTotalDowntimeSummaryByCodePerFunctionalLocationMillDashboard(
            millFilters,
            Number(selectedMachineCenterPopoverData?.machineCenterId),
            Number(selectedFailureCode?.id),
        );

    const graphData = useMemo(
        () =>
            downtimeSummaryPerFunctionalLocation
                .map((downtimeSummary) => {
                    const secondsToMinutes =
                        Math.round(downtimeSummary.summary.aggregate.sum.seconds / 60) < 1
                            ? 1
                            : Math.round(downtimeSummary.summary.aggregate.sum.seconds / 60);

                    const barValue =
                        displayIn === DisplayInOptionsEnum.MINUTES
                            ? secondsToMinutes
                            : displayIn === DisplayInOptionsEnum.PERCENT
                            ? totalDowntimeSeconds
                                ? (100 * downtimeSummary.summary.aggregate.sum.seconds) / totalDowntimeSeconds
                                : 0
                            : downtimeSummary.summary.aggregate.sum.occurrences;
                    return {
                        functionalLocationId: downtimeSummary.functional_location_id,
                        functionalLocation: handleNameTranslation(
                            i18n.language,
                            downtimeSummary.translation.translation_names,
                        ),
                        [`${selectedFailureCode?.name}`]: barValue,
                        [`${selectedFailureCode?.name}Color`]: selectedFailureType?.color_code || "",
                        totalDowntimeByMill: barValue,
                        countryCode: countryCode as string,
                    };
                })
                .sort((a, b) => Number(a.totalDowntimeByMill) - Number(b.totalDowntimeByMill))
                .slice(-5),
        [
            downtimeSummaryPerFunctionalLocation,
            displayIn,
            totalDowntimeSeconds,
            selectedFailureCode?.name,
            selectedFailureType?.color_code,
            countryCode,
            i18n.language,
        ],
    );

    return (
        <ReportCard
            showNoData={graphData?.length === 0}
            title={selectedMachineCenterPopoverData?.title || ""}
            className={classes.mainCardReportWrapper}
            headerActions={
                <IconButton
                    size="small"
                    aria-label="close"
                    onClick={() => handleSetSelectedMachineCenterPopoverData(null)}
                    className={classes.closeButton}
                >
                    <CloseIcon className={classes.closeButtonIcon} />
                </IconButton>
            }
        >
            {loading ? (
                <div className={classes.noData}>
                    <CircularProgress size={24} color="info" />
                </div>
            ) : (
                <div className={classes.graphWrapper}>
                    <ReportDowntimeBarChart
                        data={graphData || []}
                        noPercent={displayIn !== DisplayInOptionsEnum.PERCENT}
                        index="functionalLocation"
                        noTrend
                    />
                </div>
            )}
        </ReportCard>
    );
};

export default FunctionalLocationsPopover;
